import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  InputText,
  Checkbox,
  Select,
  Textarea,
  SelectItemProps,
  PaymentType,
  SelectListPosition,
  SelectType,
  ToggleInput,
  Autocomplete,
  IconUp,
  IconDown,
  IconRefresh,
} from '@ui/src';
import {
  PspAccountType,
  PspPartnerType,
  PspPartnerLocation,
} from '../../hoc/bank-and-psp/types';
import { BankAndPspInfoAccountFormTypes } from './types';
import { sortCodeRegexp } from '../../hoc/bank-and-psp/variables/schema';

import cn from './info.module.css';

const BankAndPspInfoAccountForm = ({
  control,
  errors,
  isEditable,
  itemId,
  pspIntegrations,
  setValue,
  currenciesList,
  partnerType,
  partnerLocaction,
  currencyId,
  casp,
  bankCaspId,
  accountCaspId,
  pspAccountsCategories,
  externalAccountId,
  fields,
  countries,
  setError,
  clearErrors,
  setRequestIsAvailable,
  generalInfo,
  accountId,
  bankDetailsIsDirty,
  formValues,
  sortCode,
  setSortCode,
}: BankAndPspInfoAccountFormTypes) => {
  const [currencies, setCurrencies] = useState<SelectItemProps[]>([]);
  const [caspList, setCaspList] = useState<SelectItemProps[]>([]);
  const [caspAvailable, setCaspAvailable] = useState(false);
  const [caspVisible, setCaspVisible] = useState(false);
  const [bankDetailsCollapsed, setBankDetailsCollapsed] = useState(true);

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!sortCodeRegexp.test(sortCode)) {
      setError('bankSortCode', {
        type: 'custom',
        message: 'Sort Code is incorrect',
      });
      setRequestIsAvailable(false);
    }

    if (sortCodeRegexp.test(sortCode) || !sortCode?.length) {
      clearErrors('bankSortCode');
      setRequestIsAvailable(true);
    }

    let inputValue = sortCode;

    if (e.key !== 'Backspace') {
      const regex = /.{1,2}/g;

      inputValue = sortCode
        ?.replace(/[^0-9]/g, '')
        ?.match(regex)
        ?.join('-') as string;
    }

    setSortCode(inputValue);
    setValue('bankSortCode', inputValue);
  };

  let formattedTypes: SelectItemProps[] = [];

  if (pspAccountsCategories) {
    if (partnerType === PspPartnerType.CREDIT) {
      if (
        partnerLocaction === PspPartnerLocation.DOMESTIC ||
        partnerLocaction === PspPartnerLocation.FOREIGN
      ) {
        formattedTypes = pspAccountsCategories
          ?.filter(
            (item) =>
              item.type === PspAccountType.SAFEGUARDING ||
              item.type === PspAccountType.SEGGREGATED ||
              item.type === PspAccountType.OWN_FUNDS,
          )
          ?.map((item) => ({ name: item.name, value: item.id }));
      }
    } else if (
      partnerType === PspPartnerType.EMONEY ||
      partnerType === PspPartnerType.PAYMENT
    ) {
      if (
        partnerLocaction === PspPartnerLocation.DOMESTIC ||
        partnerLocaction === PspPartnerLocation.FOREIGN
      ) {
        formattedTypes = pspAccountsCategories
          ?.filter(
            (item) =>
              item.type === PspAccountType.SEGGREGATED ||
              item.type === PspAccountType.OWN_FUNDS,
          )
          ?.map((item) => ({ name: item.name, value: item.id }));
      }
    } else if (partnerType === PspPartnerType.BLOCKCHAIN_CUSTODIAN_EXCHANGE) {
      formattedTypes = pspAccountsCategories
        ?.filter((item) => !item.type)
        ?.map((item) => ({ name: item.name, value: item.id }));
    }
  }

  useEffect(() => {
    if (casp) {
      setCaspList(
        casp?.map((item) => ({
          name:
            bankCaspId && item.value === bankCaspId && !itemId && !accountCaspId
              ? `Default from Bank (${item.name})`
              : item.name,
          value: item.value,
        })),
      );
    }
  }, [casp, bankCaspId, itemId]);

  useEffect(() => {
    if (pspIntegrations) {
      const currenciesArray: SelectItemProps[] = [];

      pspIntegrations?.pspIntegrationPayments?.forEach((item) => {
        if (
          item?.paymentsTypes?.find(
            (paymentType) => paymentType === PaymentType.CRYPTO_ASSET_PAYMENT,
          )
        ) {
          setCaspAvailable(true);
        }
      });

      pspIntegrations.pspIntegrationPayments.forEach((item) => {
        if (item.currencies.length) {
          item.currencies.forEach((currency) => {
            if (
              !currenciesArray.find(
                (itemValue) => itemValue.value === currency.currency.id,
              ) &&
              currenciesList?.find(
                (itemValue) => itemValue.value === currency.currency.id,
              )
            ) {
              currenciesArray.push({
                name: currency.currency.name,
                value: currency.currency.id,
              });
            }
          });
        } else if (!item.currencies.length) {
          currenciesList?.forEach((currency) => {
            if (
              !currenciesArray.find(
                (itemValue) => itemValue.value === currency.value,
              )
            ) {
              currenciesArray.push({
                name: currency.suffix || currency.name,
                value: currency.value,
              });
            }
          });
        }
      });

      setCurrencies(currenciesArray);
    }
  }, [pspIntegrations, currenciesList]);

  useEffect(() => {
    if (currencyId && currencyId?.length > 0) {
      currencyId?.forEach((id) => {
        if (!currenciesList?.find((item) => item.value === id)?.description) {
          setCaspVisible(true);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (caspVisible) {
            setCaspVisible(false);
          }
        }
      });
    }
  }, [currenciesList, currencyId]);

  return (
    <div className={cn.accountInfoForm}>
      <div className={cn.accountInfoFormTitle}>Enter Details</div>
      {itemId && (
        <>
          <div className={cn.accountInfoFormGroup}>
            <Controller
              control={control}
              name="visibleToClient"
              defaultValue={false}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  name="visibleToClient"
                  title="Visible by clients"
                  onChange={(e) => onChange(e.target.checked)}
                  type="checkbox"
                  checked={value}
                  disabled={Boolean(!isEditable)}
                  tabIndex={!isEditable ? -1 : 0}
                />
              )}
            />
          </div>
          <div className={cn.accountInfoFormGroup}>
            <Controller
              control={control}
              name="excludeFromTopUpIfExternalAccountAvailable"
              defaultValue={false}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  name="excludeFromTopUpIfExternalAccountAvailable"
                  title="Exclude From Top Up If External Account Available"
                  onChange={(e) => onChange(e.target.checked)}
                  type="checkbox"
                  checked={value}
                  disabled={Boolean(!isEditable)}
                  tabIndex={!isEditable ? -1 : 0}
                />
              )}
            />
          </div>
        </>
      )}
      {!itemId &&
        pspIntegrations &&
        (pspIntegrations?.name === 'PRIME_VAULT_WALLET' ||
          pspIntegrations?.name === 'OPEN_PAYD' ||
          pspIntegrations?.name === 'Keel') && (
          <div className={cn.accountInfoFormGroup}>
            <Controller
              control={control}
              name="externalAccountBindType"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <ToggleInput
                  name={name}
                  checked={value === 'ENABLED'}
                  title="Enable External Account Bind"
                  onChange={(e) =>
                    onChange(e.target.checked ? 'ENABLED' : 'DISABLED')
                  }
                  tabIndex={0}
                />
              )}
            />
          </div>
        )}
      {!itemId && (
        <div className={cn.accountInfoFormGroup}>
          <Controller
            control={control}
            name="isDefaultForExternalOperations"
            defaultValue={false}
            render={({ field: { onChange, value, name } }) => (
              <ToggleInput
                name={name}
                checked={value}
                title="Use as Default for external operations"
                onChange={(e) => onChange(e.target.checked)}
                tabIndex={0}
              />
            )}
          />
        </div>
      )}
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="realNumberOrIban"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="realNumberOrIban"
              placeholder="Enter Real Account Number or IBAN"
              onChange={onChange}
              value={value}
              title="Real Account Number or IBAN"
              error={errors?.realNumberOrIban?.message}
              disabled={
                Boolean(!isEditable) || Boolean(itemId && externalAccountId)
              }
              tabIndex={!isEditable ? -1 : 0}
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name="pspPartnerId"
        defaultValue=""
        render={({ field: { value } }) => (
          <input
            type="hidden"
            name="pspPartnerId"
            value={value}
            tabIndex={-1}
          />
        )}
      />
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="categoryId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Select
              list={
                itemId
                  ? pspAccountsCategories?.map((item) => ({
                      name: item.name,
                      value: item.id,
                    }))
                  : formattedTypes
              }
              placeholder="Choose account type"
              name="type"
              value={value}
              onChange={onChange}
              title="Account type"
              selectWrapperClassName={`${cn.accountFormSelect} ${
                errors?.categoryId?.message ? cn.accountFormSelectError : ''
              }`}
              disabled={Boolean(itemId || !isEditable)}
              tabIndex={itemId || !isEditable ? -1 : 0}
            />
          )}
        />
        {errors?.categoryId?.message && (
          <div className={cn.error}>{errors?.categoryId?.message}</div>
        )}
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="description"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Textarea
              name="description"
              placeholder="Enter description"
              onChange={onChange}
              value={value}
              title="Description"
              error={errors?.description?.message}
              disabled={Boolean(!isEditable)}
              tabIndex={itemId || !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={`${cn.accountInfoFormTitle} ${cn.currenciesTitle}`}>
        Bank details
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="useOwnBankDetails"
          defaultValue={false}
          render={({ field: { onChange, value, name } }) => (
            <ToggleInput
              name={name}
              checked={value}
              title="Override bank details for this account"
              onChange={(e) => {
                onChange(e.target.checked);

                if (!e.target.checked) {
                  setValue('bankName', generalInfo?.companyName);
                  setValue('bankLegalName', generalInfo?.legalName);
                  setValue('bankCountryId', generalInfo?.countryId);
                  setValue('bankAddress', generalInfo?.address);
                  setValue('bankSortCode', generalInfo?.sortCode);
                  setValue('bankSwift', generalInfo?.swift);
                  setValue('bankAba', generalInfo?.aba);
                  setSortCode('');
                }
              }}
            />
          )}
        />
        <div className={cn.useOwnBankDetailsHint}>
          Details are inherited from the bank (Cembra Money Bank AG). Turn on to
          set bank details that apply only to this account.
        </div>
      </div>
      <div className={cn.bankDetailsWrapper}>
        <div className={cn.bankDetailsButtonsWrapper}>
          <button
            type="button"
            className={cn.showBankDetailsButton}
            onClick={() => setBankDetailsCollapsed(!bankDetailsCollapsed)}
          >
            {bankDetailsCollapsed ? <IconDown /> : <IconUp />}
            {`${bankDetailsCollapsed ? 'Show' : 'Hide'} Bank Details`}
          </button>
          {accountId && bankDetailsIsDirty && fields?.useOwnBankDetails && (
            <button
              type="button"
              className={cn.resetButton}
              onClick={() => {
                setValue(
                  'bankName',
                  formValues?.bankName || generalInfo?.companyName,
                );
                setValue(
                  'bankLegalName',
                  formValues?.bankLegalName || generalInfo?.legalName,
                );
                setValue(
                  'bankCountryId',
                  formValues?.bankCountryId || generalInfo?.countryId,
                );
                setValue(
                  'bankAddress',
                  formValues?.bankAddress || generalInfo?.address,
                );
                setValue(
                  'bankSortCode',
                  formValues?.bankSortCode || generalInfo?.sortCode,
                );
                setValue(
                  'bankSwift',
                  formValues?.bankSwift || generalInfo?.swift,
                );
                setValue('bankAba', formValues?.bankAba || generalInfo?.aba);
              }}
            >
              <IconRefresh width={13} height={13} />
              Reset
            </button>
          )}
        </div>

        {!bankDetailsCollapsed && (
          <div className={cn.bankDetailsContent}>
            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankName"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="bankName"
                    placeholder="Enter Bank Name"
                    onChange={onChange}
                    value={value as string}
                    title="Bank Name"
                    error={errors?.bankName?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                  />
                )}
              />
            </div>
            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankLegalName"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="bankLegalName"
                    placeholder="Enter Legal Name"
                    onChange={onChange}
                    value={value as string}
                    title="Legal name"
                    error={errors?.bankLegalName?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                  />
                )}
              />
            </div>
            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankCountryId"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    name="bankCountryId"
                    placeholder="Select country"
                    list={countries || []}
                    onChange={onChange}
                    value={value as string}
                    title="Bank Country"
                    error={errors?.bankCountryId?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                    fullWidth
                  />
                )}
              />
            </div>
            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankAddress"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="bankAddress"
                    placeholder="Enter Bank Address"
                    onChange={onChange}
                    value={value as string}
                    title="Bank Address"
                    error={errors?.bankAddress?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                  />
                )}
              />
            </div>

            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankSwift"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="bankSwift"
                    placeholder="Enter Bank SWIFT/BIC"
                    onChange={onChange}
                    value={value as string}
                    title="Bank SWIFT / BIC"
                    error={errors?.bankSwift?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                  />
                )}
              />
            </div>
            <div className={cn.accountInfoFormGroup}>
              <InputText
                name="bankSortCode"
                placeholder="Enter Bank Sort code"
                onChange={(e) => {
                  setSortCode(e.target.value);

                  if (!e.target.value) {
                    setSortCode('');
                  }
                }}
                onKeyUp={onKeyUp}
                value={sortCode}
                title="Bank Sort Code"
                error={errors?.bankSortCode?.message}
                maxLength={8}
                disabled={
                  Boolean(itemId && !isEditable) || !fields?.useOwnBankDetails
                }
                tabIndex={
                  (itemId && !isEditable) || !fields?.useOwnBankDetails ? -1 : 0
                }
              />
              <Controller
                control={control}
                name="bankSortCode"
                defaultValue=""
                render={({ field: { value } }) => (
                  <input
                    type="hidden"
                    name="bankSortCode"
                    value={value as string}
                  />
                )}
              />
            </div>
            <div className={cn.accountInfoFormGroup}>
              <Controller
                control={control}
                name="bankAba"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="bankAba"
                    placeholder="Enter Bank ABA"
                    onChange={onChange}
                    value={value as string}
                    title="ABA / Routing number"
                    error={errors?.bankAba?.message}
                    disabled={
                      Boolean(!isEditable) || !fields?.useOwnBankDetails
                    }
                    tabIndex={
                      !isEditable || !fields?.useOwnBankDetails ? -1 : 0
                    }
                  />
                )}
              />
            </div>
          </div>
        )}
      </div>
      <div className={`${cn.accountInfoFormTitle} ${cn.currenciesTitle}`}>
        Intermediary Bank
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="intermediaryBankName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="intermediaryBankName"
              placeholder="Enter Intermediary Bank Name (optional)"
              onChange={onChange}
              value={value}
              title="Intermediary Bank Name"
              error={errors?.intermediaryBankName?.message}
              disabled={Boolean(!isEditable)}
              tabIndex={!isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="intermediaryBankSwift"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="intermediaryBankSwift"
              placeholder="Enter Intermediary Bank SWIFT"
              onChange={onChange}
              value={value}
              title="Intermediary Bank Swift (optional)"
              error={errors?.intermediaryBankSwift?.message}
              disabled={Boolean(!isEditable)}
              tabIndex={!isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="accountOfCorrespondentWithIntermediaryBank"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="accountOfCorrespondentWithIntermediaryBank"
              placeholder="Enter Account Of Correspondent With Intermediary Bank"
              onChange={onChange}
              value={value}
              title="Account Of Correspondent With Intermediary Bank (optional)"
              error={
                errors?.accountOfCorrespondentWithIntermediaryBank?.message
              }
              disabled={Boolean(!isEditable)}
              tabIndex={!isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={`${cn.accountInfoFormTitle} ${cn.currenciesTitle}`}>
        Supported currency
      </div>
      <div className={cn.accountInfoFormGroup}>
        <Controller
          control={control}
          name="currencyId"
          render={({ field: { onChange, value, name } }) => (
            <Select
              list={currencies}
              placeholder="Choose Currency"
              name={name}
              title="Currency"
              value={value}
              disabled={!isEditable}
              tabIndex={!isEditable ? -1 : 0}
              onChange={(e) => {
                onChange(e.target.value);

                setValue('currencyType', 'SPECIFIC');
              }}
              className={`${cn.accountFormAutocomplete} ${
                cn.accountFormAutocompleteSelect
              } ${
                errors?.currencyId?.message
                  ? cn.accountFormAutocompleteSelectError
                  : ''
              }`}
              mode={SelectType.MULTIPLE}
              selectListPosition={SelectListPosition.TOP}
              withClearButton
            />
          )}
        />
        {errors?.currencyId?.message && (
          <div className={cn.error}>{errors?.currencyId?.message}</div>
        )}
        <div className={cn.currenciesHint}>
          Defines which currencies this account supports. Doesn&apos;t create or
          remove balances
        </div>
      </div>
      {caspVisible && caspAvailable && (
        <div className={cn.accountInfoFormGroup}>
          <Controller
            control={control}
            name="caspId"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                list={caspList}
                placeholder="Choose CASP"
                name="type"
                value={value as string}
                onChange={onChange}
                title="CASP"
                selectWrapperClassName={`${cn.accountFormSelect} ${
                  errors?.caspId?.message ? cn.accountFormSelectError : ''
                }`}
                disabled={Boolean(!isEditable || !caspAvailable)}
                tabIndex={!isEditable || !caspAvailable ? -1 : 0}
                withClearButton
              />
            )}
          />
          {errors?.caspId?.message && (
            <div className={cn.error}>{errors?.caspId?.message}</div>
          )}
        </div>
      )}

      <Controller
        control={control}
        name="currencyType"
        defaultValue=""
        render={({ field: { value } }) => (
          <input type="hidden" value={value} tabIndex={-1} />
        )}
      />
    </div>
  );
};

export default BankAndPspInfoAccountForm;
